import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'
import { MobileNavigationProps } from '../types'
import { PROFILE_PATH } from '../constants'
import { getLocalizedPath, isActivePath } from '../utils'
import UserApi from 'api/rest/user'
import registrationApi from 'api/rest/registration'
import useFocusTrap from 'hooks/use-focus-trap'
import { ReactComponent as ArrowRightIcon } from '../../../images/icons/icn-arrow-right.svg'
import { ReactComponent as CloseIcon } from '../../../images/icons/icn_close.svg'
import MobileSubmenu from './MobileSubmenu'

import styles from './mobileNavigation.module.scss'
import UserAvatar from '../../profile/UserAvatar'

const MobileNavigation: React.FC<MobileNavigationProps> = ({
  isOpen,
  onClose,
  navItems,
  activeSubmenu,
  onOpenSubmenu,
  onCloseSubmenu,
  isLoggedIn,
  userProfile,
  submenuItems,
  hasPendingMatches,
  triggerElementRef,
}) => {
  const { t } = useTranslation(['strCommonUI', 'strCommon'])

  const getReturnFocusElement = () => {
    if (!triggerElementRef || typeof triggerElementRef === 'function') {
      return undefined
    }

    return triggerElementRef.current || undefined
  }

  const { trapContainerRef } = useFocusTrap<HTMLDivElement, HTMLButtonElement>({
    isActive: isOpen,
    returnFocusElement: getReturnFocusElement(),
    autoFocusDelay: 100,
  })

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  return (
    <div
      id='mobile-menu-main'
      ref={trapContainerRef}
      className={classNames(styles.mobileMenuWrapper, { [styles.open]: isOpen })}
      style={{ visibility: isOpen ? 'visible' : 'hidden' }}
      aria-hidden={!isOpen}
      data-testid='mobile-navigation'
      role='dialog'
      aria-modal='true'
    >
      <div className={styles.mobileMenuOverlay} onClick={onClose} aria-hidden='true'></div>

      <nav
        className={classNames(styles.mobileMenuNav, { [styles.submenuActive]: !!activeSubmenu })}
        aria-label={t('ariaLabel.mobileNavigation', 'Mobile navigation')}
      >
        <div className={styles.mobileMenuHeaderControls}>
          <button
            className={styles.mobileMenuCloseButton}
            onClick={onClose}
            aria-label={t('button.close', 'Close Menu')}
          >
            <CloseIcon />
          </button>
        </div>

        {!isLoggedIn && (
          <div className={styles.mobileMenuHeaderTitle}>
            <h2 className={styles.mobileMenuTitle}>{t('menu', 'Menu')}</h2>
          </div>
        )}

        {isLoggedIn && userProfile && (
          <a
            href={getLocalizedPath(PROFILE_PATH)}
            className={classNames(styles.mobileUserInfoLink, {
              [styles.withNotificationBadge]: hasPendingMatches,
            })}
            onClick={onClose}
          >
            <div className={styles.mobileUserAvatar}>
              <UserAvatar user={userProfile} size='lg' className={styles.avatarFallback} />
            </div>
            <div className={styles.mobileUserDetails}>
              <span className={styles.mobileUserName}>
                {userProfile.fullName || t('userProfile', 'User Profile')}
              </span>
              {userProfile.wtnSingles && (
                <span className={styles.mobileUserWtn}>{userProfile.wtnSingles}</span>
              )}
            </div>
            <ArrowRightIcon className={styles.mobileItemArrow} />
            {hasPendingMatches && (
              <span
                className={styles.notificationBadge}
                role='status'
                aria-label={t('ariaLabel.pendingMatches', 'you have pending matches to review')}
              ></span>
            )}
          </a>
        )}

        <ul className={styles.mobileMenuContainer}>
          {navItems.map(item => (
            <li key={item.id} className={styles.navItem}>
              <a
                href={item.hasDropdown ? '#' : getLocalizedPath(item.path)}
                className={classNames(styles.navLink, {
                  [styles.selected]: isActivePath(item.path) && !item.hasDropdown && !item.hasArrow,
                  [styles.hasArrow]: item.hasArrow || item.hasDropdown,
                })}
                onClick={e => {
                  if (item.hasDropdown) {
                    e.preventDefault()
                    onOpenSubmenu(item.name)
                  } else {
                    if (item.action) {
                      e.preventDefault()
                      item.action()
                    }
                    onClose()
                  }
                }}
              >
                <span className={styles.navText}>
                  {item.name}
                  {item.badge && <span className={styles.navBadgeMobile}>{item.badge}</span>}
                </span>
                {(item.hasArrow || item.hasDropdown) && (
                  <ArrowRightIcon className={styles.mobileItemArrow} />
                )}
              </a>
            </li>
          ))}

          {isLoggedIn && (
            <li className={classNames(styles.navItem, styles.mobileSignOutSeparator)}>
              <a
                href='#'
                className={styles.navLink}
                onClick={e => {
                  e.preventDefault()
                  UserApi.signOut()
                  onClose()
                }}
              >
                <span className={styles.navText}>{t('button.signOut')}</span>
              </a>
            </li>
          )}

          {!isLoggedIn && (
            <li className={classNames(styles.navItem, styles.separator)}>
              <div className={styles.mobileAuthLinks}>
                <button
                  className={styles.mobileAuthLink}
                  onClick={() => {
                    registrationApi.redirectToSignIn({ enableLogging: true })
                    onClose()
                  }}
                >
                  {t('button.signIn')}
                </button>
                <span className={styles.mobileAuthSeparator}> {t('or', { ns: 'strCommon' })} </span>
                <button
                  className={classNames(styles.mobileAuthLink, styles.signUpLink)}
                  onClick={() => {
                    registrationApi.redirectToRegister({ enableLogging: true })
                    onClose()
                  }}
                >
                  {t('button.register')}
                </button>
              </div>
            </li>
          )}
        </ul>
      </nav>

      <MobileSubmenu
        isActive={!!activeSubmenu}
        title={activeSubmenu}
        items={submenuItems}
        onClose={onCloseSubmenu}
        onCloseMenu={onClose}
      />
    </div>
  )
}

export default MobileNavigation
